import React, { useState, useContext, useRef } from 'react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import positionableApi from '../../../utils/api/positionable';
import uploadApi from '../../../utils/api/upload';
import { useHistory } from 'react-router';

const CalibrateMapContext = React.createContext({
  scale: 0,
  shiftX: 0,
  shiftY: 0,
  setScale: () => {},
  setShiftX: () => {},
  setShiftY: () => {},
  resetCalibration: () => {},
  saveCalibration: () => {},
  calibratedDataGroupsRef: { current: null },
  needsReupload: false,
  setNeedsReupload: () => {},
  mapId: null,
  setMapId: () => {},
  projectId: null,
  setProjectId: () => {},
  projectUploadId: null,
  setProjectUploadId: () => {},
  projectDocTypeId: null,
  setProjectDocTypeId: () => {},
});

export const useCalibrateMap = () => useContext(CalibrateMapContext);

export const CalibrateMapProvider = ({
  children,
  mapId: initialMapId,
  projectId: initialProjectId,
  projectUploadId: initialProjectUploadId,
  projectDocTypeId: initialProjectDocTypeId,
  projectUploadOrder: initialProjectUploadOrder,
  mapName,
  mapUploadId,
  onRefetchDataSources,
  onCloseCalibrateMap,
}) => {
  const history = useHistory();
  const [scale, setScale] = useState(0);
  const [shiftX, setShiftX] = useState(0);
  const [shiftY, setShiftY] = useState(0);
  const calibratedDataGroupsRef = useRef(null);
  const [needsReupload, setNeedsReupload] = useState(false);
  const [mapId, setMapId] = useState(initialMapId);
  const [projectId, setProjectId] = useState(initialProjectId);
  const [projectUploadId, setProjectUploadId] = useState(
    initialProjectUploadId,
  );
  const [projectDocTypeId, setProjectDocTypeId] = useState(
    initialProjectDocTypeId,
  );
  const [isSaving, setIsSaving] = useState(false);

  const resetCalibration = () => {
    setScale(0);
    setShiftX(0);
    setShiftY(0);
  };

  const saveCalibration = async () => {
    if (calibratedDataGroupsRef.current && !isSaving) {
      setIsSaving(true);
      const toastId = toast.loading(
        'Saving calibration it may take some time...',
      );
      const dataOnly = [];
      Object.keys(calibratedDataGroupsRef.current).forEach((groupName) => {
        dataOnly.push(...calibratedDataGroupsRef.current[groupName]);
      });

      try {
        // Save calibration changes
        await positionableApi.calibrateMapChanges(dataOnly);

        // If reupload is needed, fetch and re-upload the same file
        if (
          needsReupload &&
          mapId &&
          projectId &&
          projectUploadId &&
          projectDocTypeId
        ) {
          toast.update(toastId, {
            render:
              'Calibration saved. Optimizing map, it may take some time...',
            type: 'info',
            isLoading: true,
          });

          try {
            // Fetch the current map file
            const downloadUrl = uploadApi.getDownloadUrl(mapId);
            const response = await fetch(downloadUrl);
            const blob = await response.blob();

            // Create a File object from the blob
            const file = new File([blob], mapName, { type: blob.type });

            // Upload the new file (create new upload)
            const newUploadResponse = await uploadApi.create(file);

            // Create project upload association
            await uploadApi.project.create(
              newUploadResponse.id,
              projectId,
              projectDocTypeId,
            );

            // Prepare payload for replacing document data
            const payload = {
              newDoc: {
                ...newUploadResponse,
                project_doc_type_id: projectDocTypeId,
                project_id: projectId,
              },
              oldDoc: {
                id: mapUploadId,
                GUID: mapId,
                ProjectUploadModel: {
                  id: projectUploadId,
                  project_doc_type_id: projectDocTypeId,
                  order: initialProjectUploadOrder,
                },
              },
            };

            // Replace document data
            await uploadApi.project.replaceDocData(projectId, payload);

            toast.update(toastId, {
              render: 'Calibration saved and map optimized successfully',
              type: 'success',
              isLoading: false,
              autoClose: 3000,
            });

            // Reset the reupload flag
            setNeedsReupload(false);

            // Redirect to edit page of the new upload after a short delay
            setTimeout(() => {
              const basePath = history.location.pathname
                .split('/')
                .slice(0, -2)
                .join('/');

              window.location.assign(
                `${basePath}/${newUploadResponse.GUID}/edit?from=active-map`,
              );
            }, 1000);
          } catch (reuploadErr) {
            console.error('Error optimizing map:', reuploadErr);
            toast.update(toastId, {
              render: 'Calibration saved, but map re-upload failed',
              type: 'warning',
              isLoading: false,
              autoClose: 5000,
            });
          }
        } else {
          // Refetch all data sources
          if (onRefetchDataSources) {
            onRefetchDataSources();
          }
          resetCalibration();
          toast.update(toastId, {
            render: 'Calibration saved',
            type: 'success',
            isLoading: false,
            autoClose: 3000,
          });
                    
          // Close the calibrate map section
          if (onCloseCalibrateMap) {
            onCloseCalibrateMap();
          }
          
          setIsSaving(false);
        }
      } catch (err) {
        console.error('Error saving calibration:', err);
        toast.update(toastId, {
          render: 'Error saving calibration',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        setIsSaving(false);
      }
    }
  };

  return (
    <CalibrateMapContext.Provider
      value={{
        scale,
        shiftX,
        shiftY,
        setScale,
        setShiftX,
        setShiftY,
        resetCalibration,
        saveCalibration,
        calibratedDataGroupsRef,
        needsReupload,
        setNeedsReupload,
        mapId,
        setMapId,
        projectId,
        setProjectId,
        projectUploadId,
        setProjectUploadId,
        projectDocTypeId,
        setProjectDocTypeId,
        isSaving,
      }}
    >
      {children}
    </CalibrateMapContext.Provider>
  );
};

CalibrateMapProvider.propTypes = {
  children: PropTypes.node.isRequired,
  mapId: PropTypes.string,
  projectId: PropTypes.number,
  projectUploadId: PropTypes.number,
  projectDocTypeId: PropTypes.number,
  projectUploadOrder: PropTypes.number,
  mapName: PropTypes.string,
  mapUploadId: PropTypes.number,
  onRefetchDataSources: PropTypes.func,
  onCloseCalibrateMap: PropTypes.func,
};

const CalibrateMap = ({ isOpen }) => {
  const {
    scale,
    shiftX,
    shiftY,
    setScale,
    setShiftX,
    setShiftY,
    resetCalibration,
    saveCalibration,
    isSaving,
  } = useCalibrateMap();

  if (!isOpen) {
    return null;
  }

  return (
    <div className='calibrate-map-section'>
      <div className='calibrate-map-header'>
        <h3>CALIBRATE MAP</h3>
      </div>

      {/* Calibration Controls */}
      <div className='calibrate-map-content'>
        <p className='calibrate-map-description'>
          Adjust until legend items line up correctly on map. Changes apply to
          all legend items.
        </p>

        {/* Scale Control */}
        <div className='calibrate-control'>
          <label className='calibrate-label'>
            <span>Scale</span>
            <span className='calibrate-value'>{scale}%</span>
          </label>
          <div className='calibrate-slider-container'>
            <input
              type='range'
              min='-100'
              max='100'
              step='1'
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              onInput={(e) => {
                const min = Number(e.target.min);
                const max = Number(e.target.max);
                const value = Number(e.target.value);

                const percent = ((value - min) / (max - min)) * 100;

                e.target.style.setProperty('--value', `${percent}%`);
              }}
              className='calibrate-slider'
            />
            <div className='calibrate-slider-labels'>
              <span className='calibrate-slider-min'>-100%</span>
              <span className='calibrate-slider-max'>+100%</span>
            </div>
          </div>
        </div>

        {/* Shift Left/Right Control */}
        <div className='calibrate-control'>
          <label className='calibrate-label'>
            <span>Shift left / right</span>
            <span className='calibrate-value'>{shiftX}%</span>
          </label>
          <div className='calibrate-slider-container'>
            <input
              type='range'
              min='-100'
              max='100'
              step='1'
              value={shiftX}
              onChange={(e) => setShiftX(Number(e.target.value))}
              onInput={(e) => {
                const min = Number(e.target.min);
                const max = Number(e.target.max);
                const value = Number(e.target.value);

                const percent = ((value - min) / (max - min)) * 100;

                e.target.style.setProperty('--value', `${percent}%`);
              }}
              className='calibrate-slider'
            />
            <div className='calibrate-slider-labels'>
              <span className='calibrate-slider-min'>-100%</span>
              <span className='calibrate-slider-max'>+100%</span>
            </div>
          </div>
        </div>

        {/* Shift Up/Down Control */}
        <div className='calibrate-control'>
          <label className='calibrate-label'>
            <span>Shift up / down</span>
            <span className='calibrate-value'>{shiftY}%</span>
          </label>
          <div className='calibrate-slider-container'>
            <input
              type='range'
              min='-100'
              max='100'
              step='1'
              value={shiftY}
              onChange={(e) => setShiftY(Number(e.target.value))}
              onInput={(e) => {
                const min = Number(e.target.min);
                const max = Number(e.target.max);
                const value = Number(e.target.value);

                const percent = ((value - min) / (max - min)) * 100;

                e.target.style.setProperty('--value', `${percent}%`);
              }}
              className='calibrate-slider'
            />
            <div className='calibrate-slider-labels'>
              <span className='calibrate-slider-min'>-100%</span>
              <span className='calibrate-slider-max'>+100%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className='calibrate-actions'>
        <button 
          className='button-small bg-primary' 
          onClick={saveCalibration}
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Calibration'}
        </button>
        <button 
          className='button-small bg-tertiary' 
          onClick={resetCalibration}
          disabled={isSaving}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

CalibrateMap.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

export default CalibrateMap;
